import {FC, useState} from 'react';
import {styled} from 'styled-components';

import {FilteredResultsHeader} from '@website_frontend/components/filtered_results_header';
import {HousePreviewCard} from '@website_frontend/components/house_preview_card';
import {useDbData} from '@website_frontend/lib/db_cache';
import {useLikedIds, useAllTags} from '@website_frontend/lib/user_data_storage';

export const LikedPage: FC = () => {
  const {data} = useDbData();
  const likedIds = useLikedIds();
  const allTags = useAllTags();

  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  if (!data) {
    return (
      <Wrapper>
        <EmptyMessage>Chargement...</EmptyMessage>
      </Wrapper>
    );
  }

  if (likedIds.length === 0) {
    return (
      <Wrapper>
        <EmptyMessage>
          <EmptyTitle>Aucun favori</EmptyTitle>
          <EmptySubtitle>
            Ajoutez des favoris en cliquant sur le cœur sur les annonces
          </EmptySubtitle>
        </EmptyMessage>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {allTags.length > 0 && (
        <TagFilterBar>
          <TagChip selected={selectedTag === null} onClick={() => setSelectedTag(null)}>
            Tous
          </TagChip>
          {allTags.map(tag => (
            <TagChip key={tag} selected={selectedTag === tag} onClick={() => setSelectedTag(tag)}>
              {tag}
            </TagChip>
          ))}
        </TagFilterBar>
      )}
      {likedIds.length > 0 ? (
        <>
          <FilteredResultsHeader count={likedIds.length} />
          <Grid>
            {likedIds.map(id => (
              <HousePreviewCard key={id} id={id} />
            ))}
          </Grid>
        </>
      ) : (
        <EmptyMessage>
          <EmptyTitle>Aucun résultat</EmptyTitle>
          <EmptySubtitle>Aucun favori ne correspond au filtre sélectionné</EmptySubtitle>
        </EmptyMessage>
      )}
    </Wrapper>
  );
};

LikedPage.displayName = 'LikedPage';

const HEADER_HEIGHT = 64;
const BOTTOM_NAV_HEIGHT = 64;
const PADDING = 16;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${PADDING}px;
  padding: ${PADDING}px;
  padding-top: ${HEADER_HEIGHT + PADDING}px;
  padding-bottom: ${BOTTOM_NAV_HEIGHT + PADDING}px;

  @media (max-width: 512px) {
    padding: ${PADDING}px 0;
    padding-top: ${PADDING}px; /* no header on mobile */
    padding-bottom: ${BOTTOM_NAV_HEIGHT + PADDING}px;
  }
`;

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  justify-items: center;
  align-items: flex-start;
  gap: 20px;
`;

const EmptyMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
  color: #666;
`;

const EmptyTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const EmptySubtitle = styled.div`
  font-size: 14px;
  color: #666;
`;

const TagFilterBar = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 ${PADDING}px;
  margin-bottom: ${PADDING}px;
`;

const TagChip = styled.button<{selected: boolean}>`
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid ${props => (props.selected ? '#1976d2' : '#ccc')};
  background-color: ${props => (props.selected ? '#1976d2' : 'white')};
  color: ${props => (props.selected ? 'white' : '#333')};
  font-size: 14px;
  font-weight: ${props => (props.selected ? 600 : 400)};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => (props.selected ? '#1565c0' : '#f5f5f5')};
    border-color: ${props => (props.selected ? '#1565c0' : '#999')};
  }
`;
